import { useState, useEffect, useCallback } from 'react';
import { transfersAPI } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { 
  Plus, RefreshCw, Percent, Edit, Trash2, 
  Save, DollarSign, TrendingUp, TrendingDown, Star
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';

const FranchisePricingLevels = () => {
  const { toast } = useToast();
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLevel, setEditingLevel] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    pricing_method: 'cost_plus',
    margin_percent: 10,
    discount_percent: 0,
    description: '',
    is_default: false
  });

  const fetchLevels = useCallback(async () => {
    setLoading(true);
    try {
      const response = await transfersAPI.listPricingLevels();
      setLevels(response.data || []);
    } catch (error) {
      console.error('Error fetching pricing levels:', error);
      toast({ title: 'Error', description: 'Failed to fetch pricing levels', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchLevels();
  }, [fetchLevels]);

  const openCreateDialog = () => {
    setEditingLevel(null);
    setFormData({
      name: '',
      code: '',
      pricing_method: 'cost_plus',
      margin_percent: 10,
      discount_percent: 0,
      description: '',
      is_default: false
    });
    setDialogOpen(true);
  };

  const openEditDialog = (level) => {
    setEditingLevel(level);
    setFormData({
      name: level.name,
      code: level.code,
      pricing_method: level.pricing_method,
      margin_percent: level.margin_percent || 10,
      discount_percent: level.discount_percent || 0,
      description: level.description || '',
      is_default: level.is_default || false
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.code) {
      toast({ title: 'Error', description: 'Name and Code are required', variant: 'destructive' });
      return;
    }
    
    setSaving(true);
    try {
      if (editingLevel) {
        await transfersAPI.updatePricingLevel(editingLevel.id, formData);
        toast({ title: 'Success', description: 'Pricing level updated successfully' });
      } else {
        await transfersAPI.createPricingLevel(formData);
        toast({ title: 'Success', description: 'Pricing level created successfully' });
      }
      setDialogOpen(false);
      fetchLevels();
    } catch (error) {
      console.error('Error saving pricing level:', error);
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to save pricing level', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (level) => {
    if (!window.confirm(`Are you sure you want to delete "${level.name}"?`)) return;
    
    try {
      await transfersAPI.deletePricingLevel(level.id);
      toast({ title: 'Success', description: 'Pricing level deleted successfully' });
      fetchLevels();
    } catch (error) {
      console.error('Error deleting pricing level:', error);
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to delete pricing level', variant: 'destructive' });
    }
  };

  const getPricingMethodDisplay = (method, marginPercent, discountPercent) => {
    if (method === 'cost_plus') {
      return (
        <div className="flex items-center gap-1 text-emerald-600">
          <TrendingUp className="w-4 h-4" />
          <span>Cost + {marginPercent}%</span>
        </div>
      );
    } else if (method === 'mrp_minus') {
      return (
        <div className="flex items-center gap-1 text-blue-600">
          <TrendingDown className="w-4 h-4" />
          <span>MRP - {discountPercent}%</span>
        </div>
      );
    }
    return <span>{method}</span>;
  };

  return (
    <div className="p-6 space-y-6" data-testid="franchise-pricing-levels-page">
      <Toaster />
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Percent className="w-6 h-6 text-purple-600" />
            Franchise Pricing Levels
          </h1>
          <p className="text-gray-500 mt-1">
            Configure pricing tiers for franchise stock transfers (Cost+% or MRP-%)
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchLevels} variant="outline">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={openCreateDialog} className="bg-purple-600 hover:bg-purple-700">
            <Plus className="w-4 h-4 mr-2" />
            Add Level
          </Button>
        </div>
      </div>

      {/* Info Card */}
      <Card className="bg-purple-50 border-purple-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <DollarSign className="w-5 h-5 text-purple-600 mt-0.5" />
            <div>
              <p className="font-medium text-purple-900">How Franchise Pricing Works</p>
              <p className="text-sm text-purple-700 mt-1">
                When stock is transferred to a franchise store, the transfer price is calculated based on the pricing level assigned to that store.
                <br />
                <strong>Cost Plus:</strong> Transfer Price = Product Cost × (1 + Margin%)
                <br />
                <strong>MRP Minus:</strong> Transfer Price = Product MRP × (1 - Discount%)
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Pricing Levels Table */}
      <Card>
        <CardHeader className="border-b bg-gray-50/50 py-3">
          <CardTitle className="text-lg">Pricing Levels</CardTitle>
          <CardDescription>
            Assign these levels to franchise stores in Organization → Stores
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead>Name</TableHead>
                <TableHead>Code</TableHead>
                <TableHead>Pricing Method</TableHead>
                <TableHead className="text-center">Default</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="text-center">Status</TableHead>
                <TableHead className="text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                    Loading pricing levels...
                  </TableCell>
                </TableRow>
              ) : levels.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                    No pricing levels found. Create one to get started.
                  </TableCell>
                </TableRow>
              ) : (
                levels.map((level) => (
                  <TableRow key={level.id} data-testid={`level-row-${level.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">{level.name}</span>
                        {level.is_default && (
                          <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{level.code}</Badge>
                    </TableCell>
                    <TableCell>
                      {getPricingMethodDisplay(level.pricing_method, level.margin_percent, level.discount_percent)}
                    </TableCell>
                    <TableCell className="text-center">
                      {level.is_default ? (
                        <Badge className="bg-amber-100 text-amber-700">Default</Badge>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-gray-600 max-w-[200px] truncate">
                      {level.description || '-'}
                    </TableCell>
                    <TableCell className="text-center">
                      {level.is_active ? (
                        <Badge className="bg-emerald-100 text-emerald-700">Active</Badge>
                      ) : (
                        <Badge variant="outline" className="text-gray-500">Inactive</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => openEditDialog(level)}
                        >
                          <Edit className="w-3 h-3 mr-1" />
                          Edit
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleDelete(level)}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Percent className="w-5 h-5 text-purple-600" />
              {editingLevel ? 'Edit Pricing Level' : 'Create Pricing Level'}
            </DialogTitle>
            <DialogDescription>
              Configure pricing method for franchise stock transfers
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Level Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
                  placeholder="e.g., Standard Franchise"
                  data-testid="level-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Code *</Label>
                <Input
                  value={formData.code}
                  onChange={(e) => setFormData(p => ({ ...p, code: e.target.value.toUpperCase() }))}
                  placeholder="e.g., STD"
                  maxLength={10}
                  data-testid="level-code-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Pricing Method</Label>
              <Select 
                value={formData.pricing_method} 
                onValueChange={(v) => setFormData(p => ({ ...p, pricing_method: v }))}
              >
                <SelectTrigger data-testid="pricing-method-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="cost_plus">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-emerald-600" />
                      <span>Cost Plus (Cost + X%)</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="mrp_minus">
                    <div className="flex items-center gap-2">
                      <TrendingDown className="w-4 h-4 text-blue-600" />
                      <span>MRP Minus (MRP - Y%)</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {formData.pricing_method === 'cost_plus' ? (
              <div className="space-y-2">
                <Label>Margin Percentage (%)</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.5"
                    value={formData.margin_percent}
                    onChange={(e) => setFormData(p => ({ ...p, margin_percent: parseFloat(e.target.value) || 0 }))}
                    className="w-24"
                    data-testid="margin-percent-input"
                  />
                  <span className="text-gray-500">%</span>
                  <span className="text-sm text-gray-500">
                    (Transfer Price = Cost × {(1 + formData.margin_percent / 100).toFixed(2)})
                  </span>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <Label>Discount Percentage (%)</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.5"
                    value={formData.discount_percent}
                    onChange={(e) => setFormData(p => ({ ...p, discount_percent: parseFloat(e.target.value) || 0 }))}
                    className="w-24"
                    data-testid="discount-percent-input"
                  />
                  <span className="text-gray-500">%</span>
                  <span className="text-sm text-gray-500">
                    (Transfer Price = MRP × {(1 - formData.discount_percent / 100).toFixed(2)})
                  </span>
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))}
                placeholder="Optional description..."
                rows={2}
              />
            </div>
            
            <div className="flex items-center justify-between p-3 border rounded-lg bg-amber-50">
              <div>
                <p className="font-medium text-gray-900">Set as Default</p>
                <p className="text-sm text-gray-500">Use this level for new franchise stores</p>
              </div>
              <Switch 
                checked={formData.is_default} 
                onCheckedChange={(checked) => setFormData(p => ({ ...p, is_default: checked }))}
                data-testid="is-default-switch"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="save-level-btn"
            >
              {saving ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {editingLevel ? 'Update' : 'Create'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FranchisePricingLevels;
